import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { offlineStartup } from '../services/offlineStartup.js';
import LegacyVisitRecoveryPanel from './LegacyVisitRecoveryPanel.jsx';

export default function OfflineStartupGate({ children, startup = offlineStartup }) {
    const [state, setState] = useState({ status: 'loading' });
    const active = useRef(false);
    const running = useRef(false);

    const prepare = useCallback(async () => {
        if (running.current) return;
        running.current = true;
        setState({ status: 'loading' });
        try {
            await startup.start();
            if (active.current) setState({ status: 'ready' });
        } catch (error) {
            if (active.current) setState({ status: 'error', code: error?.code === 'legacy_review' ? 'legacy_review' : 'storage' });
        } finally {
            running.current = false;
        }
    }, [startup]);

    useEffect(() => {
        active.current = true;
        void prepare();
        return () => {
            active.current = false;
        };
    }, [prepare]);

    if (state.status === 'ready') return children;

    const loading = state.status === 'loading';

    return (
        <Box
            component="main"
            sx={{
                minHeight: '100dvh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 2.5, sm: 3 },
                background: 'radial-gradient(120% 120% at 50% 10%, #eef2ff 0%, #f8fafc 50%, #f1f5f9 100%)',
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    maxWidth: 420,
                    p: { xs: 2.25, sm: 3 },
                    borderRadius: '18px',
                    bgcolor: '#ffffff',
                    border: '1px solid rgba(226, 232, 240, 0.9)',
                    boxShadow: '0 20px 45px -15px rgba(15, 23, 42, 0.08), 0 4px 12px -2px rgba(15, 23, 42, 0.03)',
                    textAlign: 'center',
                }}
            >
                <Stack spacing={1.75}>
                    {loading ? (
                        <Stack spacing={1.5} sx={{ alignItems: 'center' }} role="status" aria-live="polite">
                            <Typography
                                sx={{
                                    fontSize: '0.6875rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.08em',
                                    color: 'primary.main',
                                    textTransform: 'uppercase',
                                }}
                            >
                                Sistema de Coordinación
                            </Typography>

                            <Box sx={{ position: 'relative', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', my: 0.5 }}>
                                <CircularProgress
                                    variant="determinate"
                                    sx={{ color: '#edf2f7' }}
                                    size={60}
                                    thickness={3.5}
                                    value={100}
                                />
                                <CircularProgress
                                    variant="indeterminate"
                                    disableShrink
                                    sx={{
                                        color: 'primary.main',
                                        position: 'absolute',
                                        animationDuration: '1400ms',
                                        [`& .MuiCircularProgress-circle`]: {
                                            strokeLinecap: 'round',
                                        },
                                    }}
                                    size={60}
                                    thickness={3.5}
                                    aria-label="Preparando almacenamiento"
                                />
                                <Box sx={{ position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <div className="brand-mark" aria-hidden="true" style={{ width: 22, height: 22 }}>
                                        <span />
                                        <span />
                                        <span />
                                    </div>
                                </Box>
                            </Box>

                            <Typography
                                component="h1"
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.1rem', sm: '1.2rem' },
                                    color: '#0f172a',
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                Preparando tus datos
                            </Typography>

                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    fontSize: '0.8125rem',
                                    lineHeight: 1.5,
                                    maxWidth: 320,
                                    mx: 'auto',
                                }}
                            >
                                Comprobando el almacenamiento y los envíos pendientes…
                            </Typography>

                            <Box
                                sx={{
                                    mt: 1,
                                    pt: 1.25,
                                    width: '100%',
                                    borderTop: '1px solid #f1f5f9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: 500 }}>
                                    Sincronización local y trabajo sin conexión
                                </Typography>
                            </Box>
                        </Stack>
                    ) : (
                        <Stack spacing={1.5}>
                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: '50%',
                                    bgcolor: '#fffbeb',
                                    border: '1px solid #fef3c7',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    color: '#d97706',
                                }}
                            >
                                <WarningAmberRoundedIcon sx={{ fontSize: 24 }} />
                            </Box>

                            <Typography
                                component="h1"
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.05rem', sm: '1.15rem' },
                                    color: '#0f172a',
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                Necesitamos revisar los datos locales
                            </Typography>

                            <Alert
                                severity="warning"
                                sx={{
                                    textAlign: 'left',
                                    borderRadius: 2,
                                    py: 0.5,
                                    px: 1.25,
                                    fontSize: '0.8rem',
                                    border: '1px solid #fde68a',
                                    bgcolor: '#fffbeb',
                                }}
                            >
                                {state.code === 'legacy_review'
                                    ? 'Hay datos de una versión anterior que requieren recuperación antes de continuar.'
                                    : 'No se pudo comprobar el almacenamiento del dispositivo.'}
                            </Alert>

                            <Typography variant="caption" sx={{ textAlign: 'left', color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.45 }}>
                                Los datos existentes se conservan. Reintentá; si el aviso continúa, contactá a Coordinación sin borrar los datos ni reinstalar la app.
                            </Typography>

                            {state.code === 'legacy_review' && (
                                <Typography variant="caption" sx={{ textAlign: 'left', color: 'text.secondary', fontSize: '0.76rem', lineHeight: 1.45 }}>
                                    Los archivos anteriores que todavía no pudieron leerse quedan pendientes de recuperación. La copia de sus datos no confirma que sus archivos estén disponibles.
                                </Typography>
                            )}

                            {state.code === 'legacy_review' && <LegacyVisitRecoveryPanel onConfirmed={prepare} />}

                            <Button
                                variant="contained"
                                onClick={() => void prepare()}
                                sx={{
                                    minHeight: 38,
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    textTransform: 'none',
                                    boxShadow: '0 4px 14px rgba(15, 91, 255, 0.25)',
                                    '&:hover': {
                                        boxShadow: '0 6px 18px rgba(15, 91, 255, 0.35)',
                                    },
                                    whiteSpace: 'normal',
                                }}
                            >
                                Reintentar
                            </Button>
                        </Stack>
                    )}
                </Stack>
            </Paper>
        </Box>
    );
}
